import React, { useState } from "react";
import { companyLabel, formatDecimal, markupExample } from "./helpers";

/**
 * Sets and lists the companies billed at something other than the global rate.
 *
 * @param {object} props - Component props.
 * @param {object[]} props.markups - Overrides currently set.
 * @param {object[]} props.companies - Companies a rate may be set for.
 * @param {string} props.globalMarkup - The configured rate, for comparison.
 * @param {Function} props.onSave - Called with the company id and the rate.
 * @param {Function} props.onDelete - Called with the company id to clear.
 * @param {boolean} props.saving - Whether a save is in flight.
 * @param {number|null} props.busyId - Company currently being cleared.
 * @returns {JSX.Element} The markup card.
 *
 * @remarks
 * Form and list sit in one card because the list is the only confirmation the
 * form has. A rate saved out of sight of the rates already set is how one
 * company quietly ends up on two different numbers in somebody's head.
 *
 * Companies on the global rate are absent rather than listed at it. The card
 * exists to show who was singled out, and every account on the default would
 * bury exactly that.
 */
const MarkupCard = ({
  markups = [],
  companies = [],
  globalMarkup,
  onSave,
  onDelete,
  saving,
  busyId,
}) => {
  const [draft, setDraft] = useState({ company_id: "", markup_percent: "" });

  const submit = (event) => {
    event.preventDefault();
    onSave(Number(draft.company_id), draft.markup_percent);
    setDraft({ company_id: "", markup_percent: "" });
  };

  const complete = draft.company_id !== "" && draft.markup_percent !== "";

  return (
    <div className="aicr-card">
      <h2>Markup by company</h2>
      <p className="aicr-card-sub">
        Overrides the markup applied when this company consumes AI. A company
        without a row here is billed at the global rate of{" "}
        {formatDecimal(globalMarkup, 3)}%. Changing a rate never alters what has
        already been charged.
      </p>

      <form onSubmit={submit}>
        <div className="aicr-grid">
          <div className="aicr-field">
            <label htmlFor="aicr-markup-company">Company</label>
            <select
              id="aicr-markup-company"
              value={draft.company_id}
              onChange={(event) =>
                setDraft({ ...draft, company_id: event.target.value })
              }
            >
              <option value="">Select a company…</option>
              {companies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {companyLabel(company)}
                </option>
              ))}
            </select>
            <span className="aicr-hint">
              Setting a rate again replaces the one already set.
            </span>
          </div>

          <div className="aicr-field">
            <label htmlFor="aicr-markup-percent">Markup (%)</label>
            <input
              id="aicr-markup-percent"
              type="text"
              inputMode="decimal"
              value={draft.markup_percent}
              onChange={(event) =>
                setDraft({ ...draft, markup_percent: event.target.value })
              }
            />
            <span className="aicr-hint">
              {markupExample(draft.markup_percent) || "Zero bills at cost."}
            </span>
          </div>
        </div>

        <div className="aicr-actions">
          <button
            type="submit"
            className="aicr-btn aicr-btn-primary"
            disabled={!complete || saving}
          >
            {saving ? "Saving…" : "Set markup"}
          </button>
        </div>
      </form>

      {markups.length === 0 ? (
        <p className="aicr-muted">
          No overrides. Every company is billed at the global rate.
        </p>
      ) : (
        <div className="aicr-table-wrap">
          <table className="aicr-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Markup</th>
                <th>A $1.00 call bills</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {markups.map((markup) => (
                <tr key={markup.companyId}>
                  <td>{markup.companyName || `Company ${markup.companyId}`}</td>
                  <td className="aicr-num">
                    {formatDecimal(markup.markupPercent, 3)}%
                  </td>
                  <td className="aicr-num">
                    ${(1 + Number(markup.markupPercent) / 100).toFixed(2)}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="aicr-btn aicr-btn-danger"
                      onClick={() => onDelete(markup.companyId)}
                      disabled={busyId === markup.companyId}
                    >
                      {busyId === markup.companyId
                        ? "Removing…"
                        : "Use global rate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MarkupCard;
